import { hasValue } from 'utils/hasValue';

export const FALLBACK_ERROR = 'Something went wrong';

const BASE_DETAIL_KEY = 'base';

type DetailValue = string | string[];

type ApiErrorBody = {
  error?: {
    code?: number;
    title?: string;
    details?: Record<string, DetailValue> | string;
  };
};

const isString = (value: unknown): value is string => typeof value === 'string';

const toMessages = (value: DetailValue): string[] =>
  (Array.isArray(value) ? value : [value]).filter(isString);

export const extractWithdrawalErrors = (error: unknown): string[] => {
  const apiError = (error as ApiErrorBody | undefined)?.error;

  if (!hasValue(apiError)) return [FALLBACK_ERROR];

  const { title, details } = apiError;

  if (isString(details)) return [details];

  if (hasValue(details) && !Array.isArray(details)) {
    const messages = Object.entries(details).flatMap(([key, value]) => {
      if (!hasValue(value)) return [];

      return toMessages(value).map((message) =>
        key === BASE_DETAIL_KEY ? message : `${key} ${message}`
      );
    });

    if (messages.length > 0) return messages;
  }

  return [isString(title) ? title : FALLBACK_ERROR];
};

const AMOUNT_DETAIL_KEY = 'amount';

// The limit could not be checked at all, so editing the amount cannot clear it.
// Matched by prefix because the currency code is interpolated (PEGO-14918).
const UNRECOVERABLE_AMOUNT_PREFIX =
  'cannot be validated against the withdrawal request limit';

// An amount rejection the user can clear by editing the amount, so it belongs on
// the field rather than in the dead-end error panel.
//
// The backend's own wording is used verbatim: it names the limit and the currency
// the cap is denominated in, neither of which the client can derive, and it stays
// correct on its own when the backend changes. Details are field-scoped fragments,
// hence the subject.
export const extractAmountFieldError = (error: unknown): string | undefined => {
  const details = (error as ApiErrorBody | undefined)?.error?.details;

  if (!hasValue(details) || isString(details)) return undefined;

  const message = toMessages(details[AMOUNT_DETAIL_KEY] ?? []).find(
    (detail) => !detail.startsWith(UNRECOVERABLE_AMOUNT_PREFIX)
  );

  return hasValue(message) ? `Amount ${message}` : undefined;
};
